"use client";

import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
} from "@/api/attachment";
import {
  getRelativePosition,
  useGetMultiplePrint,
} from "@/api/multipleprint/multipleprint";
import MultiplePrintSingleDrawer from "@/components/multipleprint/MultiplePrintSingleDrawer";
import { RelativePositionNav } from "@/components/navigation/RelativePositionNav";
import { Button } from "@/components/ui/button";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { BreadcrumbPreview } from "@/components/cms-puck/previews/BreadcrumbPreview";
import { CatalogDetailHeaderPreview } from "@/components/cms-puck/previews/CatalogDetailHeaderPreview";
import { useMultipleprintPageData } from "@/hooks/useMultipleprintPageData";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useScrollToTopOnNavigate } from "@/hooks/useScrollToTopOnNavigate";
import { SupportedLocale } from "@/i18n";
import { Multipleprint, Multiplesample, PublicationStatus } from "@/lib/types";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MultipleprintHeader } from "./MultipleprintHeader";
import { MultipleprintImages } from "./MultipleprintImages";
import { MultipleprintOverview } from "./MultipleprintOverview";
import { MultipleprintAccordions } from "./accordions";

export interface MultipleprintOverviewPageProps {
  /**
   * Id du multiple à afficher. Par défaut celui de la route
   * (`/edit/multipleprint/overview/:id`). Fourni explicitement quand la fiche
   * est montée hors route — aperçu du bloc Puck « Fiche (catalogue raisonné) ».
   */
  multipleprintId?: string;
  /**
   * Fiche montée dans une autre page (canvas de l'éditeur CMS) : on retire le
   * bandeau fixe du dashboard et la hauteur plein écran. Le corps de la fiche
   * est strictement identique — c'est ce qui garantit la parité avec le front.
   */
  embedded?: boolean;
}

/**
 * Bandeau fixe du dashboard (précédent/suivant, Fermer).
 *
 * Isolé dans son propre composant parce qu'il est le SEUL consommateur de
 * `useDashboardParams` — lequel appelle `usePagination`, qui *throw* hors de
 * `DashboardParamsProviders` (monté uniquement sous les routes `/edit`). Monter
 * ce hook depuis la fiche elle-même ferait planter tout contexte qui l'affiche
 * ailleurs, à commencer par le canvas de l'éditeur CMS avancé.
 */
function MultipleprintOverviewTopBar({
  id,
  title,
  multipleprint,
}: {
  id?: string;
  title?: string;
  multipleprint?: Multipleprint;
}) {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const navigate = useNavigate();
  const location = useLocation();
  const { relativePosition } = useDashboardParams<Multipleprint>({
    statusField: "estampesMultiplesPublicationStatus",
    resourceName: "multipleprint",
    relativePosition: id
      ? { currentId: id, positionFetcher: getRelativePosition }
      : undefined,
  });

  return (
    <div className="grid grid-cols-[1fr_5fr_2fr] items-center w-full h-[70px] bg-[#FCFCFD] border-y px-5 fixed top-0 left-0 right-0 z-40 gap-4">
      <p className="text-sm font-bold uppercase leading-tight text-gray-950 xs:hidden md:block">
        {vt("bo.preview.multipleprintTitle", "Prévisualisation estampe multiple")}
      </p>
      <RelativePositionNav
        relativePosition={relativePosition}
        buildLink={(navId) =>
          `/edit/multipleprint/overview/${navId}${location.search}`
        }
        resourceType="multipleprint"
      >
        <p className="font-semibold text-lg truncate w-fit xs:hidden md:block">
          {title}
        </p>
      </RelativePositionNav>
      <div className="flex items-center gap-3 justify-end">
        {multipleprint && (
          <MultiplePrintSingleDrawer multipleprint={multipleprint} labeledTrigger />
        )}
        <Button
          onClick={() => id && navigate(`/edit/multipleprint/${id}`)}
          variant="ghost"
          className="text-sm font-semibold text-gray-950 cursor-pointer border"
        >
          {t("Close")}
        </Button>
      </div>
    </div>
  );
}

export default function MultipleprintOverviewPage({
  multipleprintId,
  embedded = false,
}: MultipleprintOverviewPageProps = {}) {
  const { id: routeId } = useParams();
  const id = multipleprintId ?? routeId;
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const locale = (i18n.language || "fr") as SupportedLocale;
  // Hors dashboard, l'URL est celle de la page hôte (éditeur CMS) : elle ne dit
  // rien de l'exemplaire à afficher, on ne la lit donc pas.
  const searchParams = useMemo(
    () => new URLSearchParams(embedded ? "" : location.search),
    [embedded, location.search],
  );
  const requestedSampleId =
    searchParams.get("sampleId") ?? searchParams.get("exemplaireId");

  // Toujours ouvrir la prévisu en haut (le scroll du formulaire d'édition
  // est sinon conservé par la navigation SPA).
  useScrollToTopOnNavigate(
    id ? `${id}${location.search}` : undefined,
    !embedded,
  );

  const { data: multipleprint, isLoading: isLoadingMultipleprint } =
    useGetMultiplePrint(id);
  const viewContext = useMemo(() => {
    if (!multipleprint) {
      return {
        itemType: "MULTIPLE" as const,
        isExemplaire: false,
        selectedSample: null,
      };
    }

    const samples = (multipleprint.relatedMultiplesample ?? []) as Multiplesample[];
    const selectedSampleById = requestedSampleId
      ? samples.find((sample) => sample.id === requestedSampleId) ?? null
      : null;
    const publishedSample =
      multipleprint.estampesMultiplesPublicationStatus === "draft"
        ? samples.find(
            (sample) =>
              sample.exemplaireDansEstampesMultiplesDescriptionStatus ===
              "published",
          ) ?? null
        : null;
    const selectedSample = selectedSampleById ?? publishedSample;
    const isExemplaire = !!selectedSample;

    return {
      itemType: isExemplaire ? ("EXEMPLAIRE" as const) : ("MULTIPLE" as const),
      isExemplaire,
      selectedSample,
    };
  }, [multipleprint, requestedSampleId]);
  const { itemType, isExemplaire, selectedSample } = viewContext;
  const docIdForAttachments = isExemplaire
    ? selectedSample?.id
    : multipleprint?.id;
  const { data: unfilteredAttachments } =
    useGetAttachmentsByDocId(docIdForAttachments);

  const attachments = useMemo(() => {
    if (!multipleprint || !unfilteredAttachments?.length) return [];

    const publicationStatus = (
      isExemplaire
        ? selectedSample?.exemplaireDansEstampesMultiplesDescriptionStatus
        : multipleprint.estampesMultiplesPublicationStatus
    ) as PublicationStatus;

    return unfilteredAttachments.filter((a) =>
      shouldIncludeAttachment(a, publicationStatus),
    );
  }, [isExemplaire, multipleprint, selectedSample, unfilteredAttachments]);

  const {
    displayData,
    mainImage,
    otherImages,
    computedFields,
  } = useMultipleprintPageData({
    data: multipleprint,
    attachments,
    locale,
    t,
    itemType,
  });

  if (isLoadingMultipleprint) {
    return (
      <div
        className={
          embedded
            ? "w-full flex items-center justify-center py-20"
            : "w-full min-h-screen flex items-center justify-center"
        }
      >
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (!multipleprint) {
    return (
      <div className="p-4">
        <p>{t("estampeNexistePasLabel")}</p>
      </div>
    );
  }

  const registrationNumberToDisplay =
    selectedSample?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ||
    displayData?.estampesMultiplesDescriptionNumeroEnregistrement ||
    "";

  return (
    <div className={embedded ? "w-full" : "w-full min-h-screen"}>
      {!embedded && (
        <MultipleprintOverviewTopBar
          id={id}
          title={displayData?.estampesMultiplesDescriptionTitre}
          multipleprint={multipleprint}
        />
      )}
      <div className={embedded ? "w-full relative" : "w-full relative pt-[70px]"}>
        {/* Canvas CMS : bandeau + fil d'Ariane de la fiche publique (parité front). */}
        {embedded && (
          <CatalogDetailHeaderPreview
            title={
              selectedSample?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ||
              displayData?.estampesMultiplesDescriptionTitre ||
              ""
            }
            resourceType="multipleprint"
          />
        )}
        <div className="w-full max-w-screen-2xl m-auto md:p-10 xs:p-5">
          {embedded && (
            <div className="hidden md:block">
              <BreadcrumbPreview
                currentLabel={
                  displayData?.estampesMultiplesDescriptionTitre || undefined
                }
              />
            </div>
          )}
          <div className="flex xs:flex-col md:flex-row items-start justify-center text-center gap-10">
            <MultipleprintImages
              mainImage={mainImage}
              otherImages={otherImages}
              data={displayData}
              locale={locale}
              t={t}
              dimensionsSummaryCalculated={computedFields.dimensionsSummaryCalculated}
              supportTechniqueCalculated={computedFields.supportTechniqueCalculated}
              itemType={itemType}
              isExemplaire={isExemplaire}
              selectedSample={selectedSample}
            />
            <div className="w-full h-full flex flex-col gap-5 text-left md:pr-16">
              <div className="flex items-center justify-between">
                <p className="font-normal text-base subpixel-antialiased">
                  {computedFields.artistsCalculated}
                </p>
              </div>
              <MultipleprintHeader
                title={displayData?.estampesMultiplesDescriptionTitre || ""}
                registrationNumber={registrationNumberToDisplay}
                sampleNumber={
                  selectedSample?.exemplaireDansEstampesMultiplesDescriptionNumero
                }
                mainImageName={mainImage?.name ?? null}
                t={t}
                itemType={itemType}
              />
              <MultipleprintOverview
                data={displayData}
                dimensionsSummaryCalculated={
                  computedFields.dimensionsSummaryCalculated
                }
                supportTechniqueCalculated={
                  computedFields.supportTechniqueCalculated
                }
                signatureCalculated={computedFields.signatureCalculated}
                inscriptionDosCalculated={
                  computedFields.inscriptionDosCalculated
                }
                locale={locale}
                t={t}
                itemType={itemType}
                isExemplaire={isExemplaire}
                selectedSample={selectedSample}
                embedded={embedded}
              />
              <MultipleprintAccordions
                data={displayData}
                computedFields={computedFields}
                locale={locale}
                t={t}
                itemType={itemType}
                selectedSample={selectedSample}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
